// Per-viewer "recently viewed" list for the spotlight empty state (#1504),
// persisted in localStorage. It's a convenience, not a source of truth:
// every access is guarded, since localStorage can throw (private windows,
// blocked site data) and must never break the page. Items can go stale (a
// record gets deleted); clicking one then lands on the site's 404.

export type RecentlyViewedType = 'event' | 'artist' | 'area' | 'dance_style' | 'post' | 'organizer' | 'venue';

export interface RecentlyViewedItem {
  type: RecentlyViewedType;
  slug: string;
  title: string;
  url: string;
  imageUrl?: string | null;
  viewedAt: number;
}

export const RECENTLY_VIEWED_LIMIT = 8;
export const RECENTLY_VIEWED_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'wcwd_recently_viewed';

const isItem = (value: unknown): value is RecentlyViewedItem => {
  const v = value as RecentlyViewedItem;
  return (
    !!v &&
    typeof v.type === 'string' &&
    typeof v.slug === 'string' &&
    typeof v.title === 'string' &&
    typeof v.url === 'string' &&
    typeof v.viewedAt === 'number'
  );
};

export function getRecentlyViewed(): RecentlyViewedItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - RECENTLY_VIEWED_MAX_AGE_MS;
    return parsed.filter(isItem).filter((i) => i.viewedAt >= cutoff);
  } catch {
    return [];
  }
}

function save(items: RecentlyViewedItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore — see header */
  }
}

// Newest first; re-viewing moves an item to the top with fresh details.
export function addRecentlyViewed(item: RecentlyViewedItem): void {
  const rest = getRecentlyViewed().filter((i) => !(i.type === item.type && i.slug === item.slug));
  save([item, ...rest].slice(0, RECENTLY_VIEWED_LIMIT));
}

export function clearRecentlyViewed(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — see header */
  }
}

// ── Show pages → items ─────────────────────────────────────────────────────
// One entry per Inertia show page: which prop holds the record, and which of
// its fields are the title and image. Prop shapes are the controllers'.

type PageRecord = { [key: string]: unknown };

interface Extractor {
  type: RecentlyViewedType;
  prop: string;
  title: (record: PageRecord) => unknown;
  image?: (record: PageRecord) => unknown;
}

const stripAreaSuffix = (record: PageRecord) => {
  const name = String(record.name ?? '');
  return record.strip_area_suffix ? name.replace(/ Area$/, '') : name;
};

const EXTRACTORS: Record<string, Extractor> = {
  'Events/EventShow': { type: 'event', prop: 'event', title: (r) => r.name, image: (r) => r.imageUrl },
  'Artists/ArtistShow': { type: 'artist', prop: 'artist', title: (r) => r.name, image: (r) => r.avatar_url },
  'Areas/AreaShowView': { type: 'area', prop: 'area', title: stripAreaSuffix },
  'DanceStyles/DanceStyleShowView': { type: 'dance_style', prop: 'danceStyle', title: (r) => r.name },
  'Posts/PostShow': { type: 'post', prop: 'post', title: (r) => r.title, image: (r) => r.featured_image_url },
  'Organizer/OrganizerShow': {
    type: 'organizer',
    prop: 'organizer',
    title: (r) => r.name,
    image: (r) => r.organization_image_url,
  },
  'Venues/VenueShow': { type: 'venue', prop: 'venue', title: (r) => r.name },
};

interface InertiaPageLike {
  component: string;
  props: PageRecord;
}

export function recentlyViewedFromPage(page: InertiaPageLike, pathname: string): RecentlyViewedItem | null {
  const extractor = EXTRACTORS[page.component];
  if (!extractor) return null;

  const record = page.props[extractor.prop] as PageRecord | undefined;
  const title = record && extractor.title(record);
  if (!record || typeof record.slug !== 'string' || typeof title !== 'string' || !title) return null;

  const image = extractor.image?.(record);
  return {
    type: extractor.type,
    slug: record.slug,
    title,
    url: pathname,
    imageUrl: typeof image === 'string' && image ? image : null,
    viewedAt: Date.now(),
  };
}

export function recordPageView(page: InertiaPageLike, pathname: string = window.location.pathname): void {
  const item = recentlyViewedFromPage(page, pathname);
  if (item) addRecentlyViewed(item);
}
